import React, { useRef, useEffect, useState, useMemo } from 'react';
import { bindActionCreators } from 'redux';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FileUploader } from 'react-drag-drop-files';
import QueryString from 'query-string';
import * as dayjs from 'dayjs';
import { sanitizeHtml, isHtmlEquivalent } from 'pages/Notes/utils/html';

import {
  pushSiteNotification,
  updateLoginSavedColors as updateLoginSavedColorsAction,
} from 'containers/App/actions';
import {
  getNoteBlockedStatus,
  getPlanningCategory,
  getPageBranding,
  updateUseTemplateFileUpload,
} from 'apollo/notes.query';
import * as noteActions from 'containers/ProjectTools/Notes/Note/actions';
import { Loading } from 'components/Loading';
import Accordion from 'components/Accordion';
import APModal from 'components/Modal/APModal';
import Clickwrap from 'components/Clickwrap';
import FormSelect from 'components/FormFields/FormSelect';
// import Discussion from 'components/Discussion';
import ImageUploadField from 'components/FormFields/ImageUploadField';
import { getUsedColors } from 'components/PlateEditor/utils/extractUsedColors';
import FileViewer from 'components/FileViewer';
import { useAutosizeTextarea } from 'hooks/useAutosizeTextarea';
import { NOTE_RESTRICTIONS, MAX_NOTE_NAME_LENGTH as maxTitleLength } from 'utils/constants';
import noteEditorIcon from 'assets/images/notes/note-editor.svg';
import DeleteButton from 'components/Button/DeleteButton';
import { deserializeTinyMCE, serializeAsHtml } from 'components/PlateEditor';
import UploaderIcon from 'assets/images/uploader.svg';
import { saveStorage, getStorage, removeStorage } from 'components/PlateEditor/utils/localStorage';
import UserActivity from './UserActivity';
import { NoteEditorActionButton } from './NoteEditor.ActionButton';
import { InactivityModal } from './InactivityModal';
import Note from './Note';

import './note-editor.scss';

const customParseFormat = require('dayjs/plugin/customParseFormat');

const AUTOCLOSE_MINUTES = 10;
const AUTOCLOSE_SECONDS = AUTOCLOSE_MINUTES * 60;
const AUTOCLOSE_WARNING_SECONDS = 120;
const AUTOSAVE_SECONDS = 2;
const AUTOSAVE_TIME = AUTOCLOSE_SECONDS - AUTOSAVE_SECONDS;

const generateHeaderText = ({ title, name, date, align }) => {
  dayjs.extend(customParseFormat);
  const formattedDate = date ? dayjs(date).format('MMMM D, YYYY') : null;
  const brandingHtml = date ? (
    <>
      {title}<br />
      {name}<br />
      {formattedDate}
    </>
  ) : (
    <>{title}</>
  );
  const brandingJson = {
    type: 'note-header',
    align,
    children: [
      { text: date ?
        `${title}\n${name}\n${formattedDate}\n` :
        `${title}\n`,
      },
    ],
  };

  return { brandingHtml, brandingJson };
};

const AutosizeTextarea = (props) => {
  const { textareaRef } = useAutosizeTextarea({ enterToSubmit: true });
  return (
    <textarea
      ref={textareaRef}
      maxLength={maxTitleLength}
      {...props}
    />
  );
};

const NoteEditorComponent = ({
  isTemplate,
  isCategory,
  category,
  itemId,
  currentItem,
  updateItem,
  blockCurrentNote,
  unBlockCurrentNote,
  fileUpload,
  fileRemove,
  restrictionsNote,
  // createNoteComment,
  // updateNoteComment,
  // deleteNoteComment,
  closeErrorModal,
  notesEdit,
}) => {
  const history = useHistory();
  const queryParams = QueryString.parse(history.location?.search);
  const { noteId: noteIdParam, templateId: templateIdParam } = useParams();

  const dispatch = useDispatch();
  const { pushNotification, updateLoginSavedColors } = bindActionCreators({
    pushNotification: pushSiteNotification,
    updateLoginSavedColors: updateLoginSavedColorsAction,
  }, dispatch);
  const { updateUseFileUpload } = bindActionCreators({ ...noteActions }, dispatch);

  const isMasquerading = useSelector((state) => state.global.userInfo.isMasquerading);
  // const userId = useSelector((state) => state.global.userInfo.id);
  const orgId = useSelector((state) => state.global.userInfo.orgId);

  const currentItemHtml = useSelector((state) =>
    isTemplate ? state.projectTools.note.template?.noteHtml : state.projectTools.note.note?.noteHtml);
  // const comments = useSelector((state) => state.projectTools.note.note?.comments);
  const noteError = useSelector((state) => state.projectTools.note.error);
  const loading = useSelector((state) => state.projectTools.note.loading);
  const project = useSelector((state) => state.projectTools.project);
  const projectId = currentItem?.weddingId;

  // Permission to reset note.
  const canResetNote = isMasquerading && currentItem?.noteJson?.originalHtml;

  const [noteTitle, setNoteTitle] = useState(currentItem.title);
  const [isEdit, setEditMode] = useState(false);
  // Because of the effect hook, the save api call will happen on load
  // which can overwrite data unintentionally. We only want to allow the
  // save call to happen if the user has switched to editing mode.
  const [hasBeenEdited, setHasBeenEdited] = useState(false);
  const [useFileUpload, setUseFileUpload] = useState(false);
  const [useDefaultHeader, setUseDefaultHeader] = useState(false);
  const [restriction, setRestriction] = useState('None');
  const [openMultiUserModal, setMultiUserModalOpen] = useState(false);
  const [content, setContent] = useState(null);
  const [html, setHtml] = useState(null);
  const [currentEditorName, setCurrentEditorName] = useState('');
  const [isImageUpdating, setImageUpdating] = useState(false);
  const [imageKey, setImageKey] = useState(currentItem?.image?.key);
  const [imageUrl, setImageUrl] = useState(currentItem?.imageUrls?.rect260x175?.url);
  const [imageError, setImageError] = useState('');
  const [originalImageKey, setOriginalImageKey] = useState(currentItem?.originalImage?.key);
  const [showCropper, setShowCropper] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [branding, setBranding] = useState(null);
  const [titleEditMode, setTitleEditMode] = useState(queryParams.edit === 'true');
  const [selectTitle, setSelectTitle] = useState(queryParams.edit === 'true');
  const [showRevertWarning, setShowRevertWarning] = useState(false);
  const [didNoteRecoverFromBackup, setDidNoteRecoverFromBackup] = useState(false);

  const { clientReadOnly, plannerOnly } = currentItem;
  const userOrgId = useSelector((state) => state.global.userInfo.orgId);
  const projectOrgId = useSelector((state) => state.projectTools.project?.organizationId);

  // Get and update saved colors from login preferences.
  const savedColors = useSelector((state) => state.global.userInfo.savedColors);
  const saveColor = (color) => {
    const colors = [...savedColors, color];

    updateLoginSavedColors(colors);
  };

  const deleteColor = (color) => {
    const colors = savedColors.filter(_color => _color !== color);

    updateLoginSavedColors(colors);
  };

  // Maintain used colors within the note.
  const usedColors = useMemo(() => {
    const { currentVersion } = content || {};
    if (!currentVersion) return [];
    return getUsedColors(currentVersion);
  }, [content?.currentVersion]);

  const toolbarProps = {
    colorPickerProps: {
      usedColors,
      savedColors,
      saveColor,
      deleteColor,
    },
  };

  const metadata = {
    imageUploadPath: isTemplate ? `organizations/${orgId}/note-template/${itemId}/uploads` :
      `weddings/${projectId}/note/${itemId}/uploads`,
  };

  const isClient = userOrgId !== projectOrgId;
  // Templates aren't associated with projects so checking ownership is different.
  const isOwner = userOrgId === projectOrgId ||
    (isTemplate && currentItem.organizationId === userOrgId);

  // Saving state
  const [isSaving, setIsSaving] = useState(false);

  // Autosave states
  const [inactivityTime, setInactivityTime] = useState(0);
  const [isAutosaving, setIsAutosaving] = useState(false);
  const [showInactivityModal, setShowInactivityModal] = useState(false);
  const ticker = useRef();
  const lastDateEdited = useRef();
  const didAutosave = useRef();

  // Variables to handle migration from v1 to v2 notes.
  const [brandingContent, setBrandingContent] = useState(null);

// Clients with the restriction=client editing locked cannot edit note.
// Clients without notes.edit permission cannot edit note.
  const editingLocked = !isOwner && (clientReadOnly || !notesEdit);

  const updateImageKey = (newImageKey) => {
    if (!newImageKey) {
      updateItem({
        id: currentItem.id,
        noteBody: { ...currentItem, imageKey: 'delete' },
      });
      setImageUpdating(false);
    }
    else if (imageKey !== newImageKey) {
      updateItem({
        id: currentItem.id,
        noteBody: { ...currentItem, imageKey: newImageKey },
      });
      setImageUpdating(false);
    };
    setImageKey(newImageKey);
  };

  const getRestrictionValue = () => {
    if(currentItem.clientReadOnly && currentItem.plannerOnly) {
      return 'None';
    } if (currentItem.clientReadOnly && !currentItem.plannerOnly) {
      return 'ClientEditingLocked';
    } if (!currentItem.clientReadOnly && currentItem.plannerOnly) {
      return 'VisibleToOrgMembersOnly';
    };
    return 'None';
  };

  const toggleDefaultHeader = () => {
    const value = !useDefaultHeader;
    setUseDefaultHeader(value);
    updateItem({
      id: currentItem.id,
      noteBody: {
        weddingId: currentItem.weddingId,
        showBrandingHeader: value,
      },
    });
  };

  const handleRevertNote = () => {
    setShowRevertWarning(true);
  };

  // Admin function to revert note back to original state before migration.
  // Should not be accessible for non-admin users or if note was not migrated.
  const revertNote = () => {
    editDone();
    if (currentItem?.noteJson?.originalHtml && currentItem?.noteJson?.isMigrated) {
      updateItem({
        id: currentItem.id,
        noteBody: {
          ...currentItem,
          noteHtml: currentItem.noteJson.originalHtml,
          noteJson: {
            ...currentItem.noteJson,
            currentVersion: null,
            isMigrated: false,
          },
        },
      });
    }
  };

  const goBack = () => {
    if (isTemplate) {
      window.location.href = `/app/pro/${orgId}/settings/templates/notes-templates`;
    } else if (isCategory) {
      window.location.href = `/app/project/${projectId}/category/${category}`;
    } else {
      window.location.href = `/app/project/${projectId}/tools/notes`;
    }
  };

  const handleBack = async () => {
    if (isEdit) {
      if (!isTemplate) unBlockCurrentNote({ variables: { id: itemId } });
      await saveNoteContents();
    }
    goBack();
  };

  useEffect(() => {
    // Have to deal with cross-origin script urls.
    const scriptUrl = new URL('assets/scripts/ticker.js', import.meta.url);
    const workerContent = `importScripts( '${scriptUrl}' );`;
    const workerUrl = URL.createObjectURL(new Blob([workerContent], { type: 'text/javascript' }));

    // Init timer for autosaving.
    ticker.current = new Worker(workerUrl);

    // Clean up object url.
    URL.revokeObjectURL(workerUrl);

    // Start worker process.
    ticker.current.addEventListener('message', handleTicker);
  }, []);

  useEffect(() => {
    async function fetchBranding() {
      const _orgId = isTemplate ? orgId : projectOrgId;
      const pageBranding = await getPageBranding(_orgId);
      setBranding(pageBranding?.data?.pageBranding[0]);
    }
    if (projectOrgId || (isTemplate && orgId)) {
      fetchBranding();
    }
  }, [projectOrgId]);

  useEffect(() => {
    const getCategoryOptions = async () => {
      const response = await getPlanningCategory(projectId);
      if (response?.data?.planningCategory) {
        const cats = response.data.planningCategory.slice();
        // Alphabetical sort.
        const sorted = cats.sort((a, b) => {
          // Force labels to be lowercase so uncaptialized words get sorted correctly.
          const aCat = a.category.toLowerCase();
          const bCat = b.category.toLowerCase();
          if (aCat < bCat) return -1;
          if (aCat > bCat) return 1;
          return 0;
        });

        setCategoryOptions(sorted);
      }
    };
    if (projectId) {
      getCategoryOptions();
    }
  }, [projectId]);

  // Initialize and set note variables.
  useEffect(() => {
    // Need to wait until both note and branding is loaded.
    // If note uses default header, we don't want to see the layout shift
    // if the note content loads before the branding. If content is set,
    // that means initial load has happened.
    if (!content && currentItem && branding) {
      const {
        title,
        image,
        imageUrls,
        originalImage,
        showBrandingHeader,
        isFileUpload,
        updatedAt,
      } = currentItem;
      setNoteTitle(title);
      setRestriction(getRestrictionValue());
      setImageKey(image?.key);
      setImageUrl(imageUrls?.rect260x175?.url);
      setOriginalImageKey(originalImage?.key);
      setUseDefaultHeader(showBrandingHeader);
      setUseFileUpload(isFileUpload);

      /* ---- Set up content for note editor. ---- */
      // 1. Determine if note crashed and recover from local storage if so.
      const recoveredNote = didNoteCrash(noteIdParam, templateIdParam, updatedAt);
      if (recoveredNote && recoveredNote.length) {
        setDidNoteRecoverFromBackup(true);
        setContent({ currentVersion: recoveredNote });
        // Automatically set the note to editing.
        setEditMode(true);
        setHasBeenEdited(true);
        // Let the user know they have recovered.
        pushNotification({
          message: 'Note was recovered from an error.',
          time: 3000,
        });
      }
      // 2. Does not need migration because there is a current version.
      else if (currentItem.noteJson?.currentVersion) {
        setContent(currentItem.noteJson);
      }
      // 3. Needs migration because it has existing html, but no current version.
      else if (currentItem.noteHtml) {
        try {
          // Convert existing html to slate format.
          let slateContent = deserializeTinyMCE(currentItem.noteHtml);

          // Migrate branding header as editor content, if checked.
          if (currentItem.showBrandingHeader) {
            const { heading_align = 'center' } = branding.templateDict || {};
            const { brandingHtml, brandingJson } = generateHeaderText({
              title: currentItem.title,
              name: project?.name,
              date: project?.weddingDate,
              align: heading_align,
            });

            setBrandingContent(brandingHtml);
            slateContent = [brandingJson, ...slateContent];
          }

          // Add original html to the json object as a backup.
          setContent({
            originalHtml: currentItem.noteHtml,
            currentVersion: slateContent,
            isMigrated: false,
          });
        } catch(e) {
          // Show pop up let user know migration failed.
          // #TODO: create error state modal
          // console.log('error', e);
        }
      }
      // 4. Is a new note so set [] because that's empty slate content.
      else {
        setContent([]);
      }
    }

    // If note changes from autosave, display toast notif and dismiss
    // autosave state.
    if (isAutosaving) {
      pushNotification({
        message: 'Note was automatically saved.',
        time: 3000,
      });
      setIsAutosaving(false);
    }

    // If note crashes, content should already be loaded, so we won't enter the previous condition.
    // So we need to make sure to retain original version from note in the db, if it exists.
    if (didNoteRecoverFromBackup && content && currentItem) {
      // Check if original note exists
      if (currentItem.noteJson?.originalHtml) {
        setContent({
          ...content,
          originalHtml: currentItem.noteJson?.originalHtml,
        });
      }
    }

    // Reset crash state when note finishes saving.
    if (isSaving) {
      // Delete local storage to prevent version conflicts.
      if (didNoteRecoverFromBackup) setDidNoteRecoverFromBackup(false);
      removeStorage(isTemplate ? `note-template-${currentItem.id}` : `note-${currentItem.id}`);
      setIsSaving(false);
    }
  }, [currentItem, branding]);

  useEffect(() => {
    if (currentItemHtml) {
      setHtml(sanitizeHtml(currentItemHtml));
    }
  }, [currentItemHtml]);

  // Save updated html.
  useEffect(() => {
    if (hasBeenEdited && !isHtmlEquivalent(html, currentItem.noteHtml)) {
      setIsSaving(true);
      updateItem({
        id: currentItem.id,
        noteBody: { ...currentItem, noteHtml: html, noteJson: content },
      });
    }
  }, [html]);

  if (!currentItem) return <Loading />;

  const filename = currentItem?.file?.originalFilename;
  const { image, createdAt, createdBy, updatedAt, updatedBy } = currentItem;

  const updateCategoryOption = (e) => {
    e.stopPropagation();
    updateItem({
      id: currentItem.id,
      noteBody: { ...currentItem, categoryId: Number(e.currentTarget.value) },
    });
  };

  const restrictionOnChange = (e) => {
    e.stopPropagation();
    setRestriction(e.currentTarget.value);
    switch (e.currentTarget.value) {
      case 'VisibleToOrgMembersOnly':
        if (isTemplate) {
          updateItem({
            id: currentItem.id,
            noteBody: { ...currentItem, clientReadOnly: false, plannerOnly: true },
          });
        } else {
          restrictionsNote({ id: currentItem?.id, clientReadOnly: false, plannerOnly: true });
        }
        break;
      case 'ClientEditingLocked':
        if (isTemplate) {
          updateItem({
            id: currentItem.id,
            noteBody: { ...currentItem, clientReadOnly: true, plannerOnly: false },
          });
        } else {
          restrictionsNote({ id: currentItem?.id, clientReadOnly: true, plannerOnly: false });
        }
        break;
      case 'None':
        if (isTemplate) {
          updateItem({
            id: currentItem.id,
            noteBody: { ...currentItem, clientReadOnly: false, plannerOnly: false },
          });
        } else {
          restrictionsNote({ id: currentItem?.id, clientReadOnly: false, plannerOnly: false });
        }
        break;
      default:
        break;
    };
  };

  const editModeChange = async () => {
    // Once editing starts, do not display faked header if note is being migrated.
    if (content?.isMigrated === false) {
      setContent({
        ...content,
        isMigrated: true,
      });
    }

    if (isTemplate) {
      setHasBeenEdited(true);
      setEditMode(true);
      return;
    }

    const response = await getNoteBlockedStatus(currentItem.id);
    const { isBlocking, noteJson, updatedAt: _updatedAt } = response.data.note || {};
    if (_updatedAt > currentItem.updatedAt) {
      setContent(noteJson);
    }
    if (isBlocking === false) {
      setHasBeenEdited(true);
      setEditMode(true);
      blockCurrentNote({ variables:{ id: itemId } });
    } else {
      setCurrentEditorName(response?.data?.note?.blockedBy?.fullname || '');
      setMultiUserModalOpen(true);
    }
  };

  const editDone = async () => {
    saveNoteContents();
    if (!isTemplate) unBlockCurrentNote({ variables: { id: itemId } });
    setEditMode(false);
    // Stop the timer.
    ticker.current.postMessage('stop');
  };

  // External function that isn't cached inside of message event listener.
  const triggerInactivityModal = () => {
    if (!showInactivityModal) setShowInactivityModal(true);
  };

  // External function that isn't cached inside of message event listener.
  const dismissInactivityModal = () => {
    setShowInactivityModal(false);
  };

  // Close after inactivity.
  const inactivityClose = () => {
    editDone();
  };

  const startTicker = () => {
    if (ticker.current) {
      lastDateEdited.current = new Date();
      ticker.current.postMessage('start');
    }
    didAutosave.current = false;
  };

  const stopTicker = () => {
    if (ticker.current) {
      ticker.current.postMessage('stop');
    }
  };

  // #TODO: There's opportunity to create versioning for notes via json object. For now, just storing
  // note content in { currentVersion: content }.
  const updateNoteContent = (newContent) => {
    setContent({
      ...content,
      currentVersion: newContent,
    });

    if (isEdit) {
      // Save the note to local storage so that we can recover from a crash in the editor.
      saveStorage(
        isTemplate ? `note-template-${currentItem.id}` : `note-${currentItem.id}`,
        {
          timestamp: dayjs().toISOString(),
          content: newContent,
        },
      );
      startTicker();
    }
  };

  const handleTicker = () => {
    const now = new Date();
    const remaining = Math.floor(AUTOCLOSE_SECONDS - ((now.getTime() - lastDateEdited.current.getTime()) / 1000));
    const shouldTriggerAutosave = !didAutosave.current;
    // #TODO: might want to refactor so that the ticker doesn't cause a rerender on every tick.
    setInactivityTime(remaining);
    if (remaining <= 0) {
      // Stop the ticker and close the editor.
      stopTicker();
      inactivityClose();
    }
    if (remaining < AUTOCLOSE_WARNING_SECONDS) {
      // Open a modal to warn user about autoclosing the note.
      triggerInactivityModal();
    }
    if (shouldTriggerAutosave && remaining < AUTOSAVE_TIME) {
      // Attempt autosave after 1 seconds of last activity.
      // Note: Save api call won't fire until generated html is different.
      triggerAutosave();
      // Update ref so this conditional isn't triggered again.
      didAutosave.current = true;
    }
  };

  // Need to use state + effect to autosave because autosaving functions
  // are wrapped in event listener, so calling save function directly will
  // use a cached version of the state.
  const triggerAutosave = () => {
    setIsAutosaving(true);
  };

  useEffect(() => {
    if (isAutosaving) {
      saveNoteContents();
    }
  }, [isAutosaving]);

  // Save note contents.
  const saveNoteContents = async () => {
    if (!useFileUpload && content?.currentVersion) {
      const newHtml = await deserialize(content.currentVersion);
      setHtml(sanitizeHtml(newHtml));

      if (newHtml === currentItem.noteHtml) {
        // Always ensure local storage note gets removed when the user is done.
        removeStorage(isTemplate ? `note-template-${currentItem.id}` : `note-${currentItem.id}`);
      }
    }
    return Promise.resolve();
  };

  // Deserialize current note json.
  const deserialize = (data) => {
    return new Promise((resolve, reject) => {
      try {
        const newHtml = serializeAsHtml(data);
        resolve(newHtml);
      } catch (error) {
        reject(error);
      }
    });
  };

  // const createComment = ({ body }) => {
  //   createNoteComment({
  //     body,
  //     kind: 'user',
  //     noteId: itemId,
  //   });
  // };

  // const updateComment = ({ id, body }) => {
  //   updateNoteComment({
  //     body,
  //     id,
  //     kind: 'user',
  //     noteId: itemId,
  //   });
  // };

  // const deleteComment = ({ id }) => {
  //   deleteNoteComment({ id, noteId: itemId });
  // };

  const handleFileUpload = (file) => {
    if (!file || file.type === 'click') return;
    fileUpload({
      variables: {
        filename: file.name,
        noteId: itemId,
        file,
        isTemplate,
      },
    });
  };

  const handleFileRemove = () => {
    fileRemove({ variables: { id: itemId } });
  };

  const closeError = () => {
    closeErrorModal();
    if (isAutosaving) {
      setIsAutosaving(false);
    }
  };

  const handleImageUploadError = (value) => {
    const { acceptType } = value || {};
    if (acceptType) {
      setImageError('Please upload your image in one of the following formats: JPEG or PNG.');
    }
  };

  const renameTitle = (e) => {
    const { value } = e.target;
    const trimmed = value?.trim();

    // To prevent previous title from flashing before request finishes.
    setNoteTitle(trimmed);

    // Revert title to original if textarea is empty.
    if (trimmed === '') {
      setNoteTitle(currentItem.title);
    }
    else if (trimmed && trimmed !== currentItem?.title) {
      updateItem({
        id: currentItem.id,
        noteBody: { ...currentItem, title: value },
      });
    }

    setTitleEditMode(false);
    setSelectTitle(false);
  };

  const handleTitleFocus = (event) => {
    if (selectTitle) {
      event.target.select();
    } else {
      event.target.selectionStart = event.target.value.length;
    }
  };

  if (branding === null || content === null) {
    return (
      <Loading />
    );
  }

  if (!isOwner && plannerOnly) {
    return (
      <span className="content-container notes-container notes-editor">
        Sorry about this, but you don't have permission to access this page.
      </span>
    );
  }

  return (
    <div className={clsx(
        'content-container',
        'notes-container',
        'notes-editor',
        isTemplate && 'notes-template-editor',
        isEdit && 'notes-editor--editing',
      )}
    >
      <section className="page-top">
        <div className="page-back">
          <button
            className="back-button"
            onClick={handleBack}
          >
            <FontAwesomeIcon icon={['fa', 'arrow-left']} />
            {isTemplate ? 'Back' : isCategory ? `Back to ${category}` : 'Back To Notes'}
          </button>
        </div>
        <div className="page-top__actions">
          <NoteEditorActionButton
            isTemplate={isTemplate}
            id={currentItem.id}
            note={currentItem}
            isOwner={isOwner}
            clientReadOnly={clientReadOnly}
            notesEdit={notesEdit}
          />
        </div>
      </section>
      <section className="page-title page-title--actions">
        {titleEditMode ? (
          <AutosizeTextarea
            className="note-title__textarea unstyled"
            placeholder="Add Note Title"
            defaultValue={
              currentItem.title.length <= maxTitleLength || !maxTitleLength
              ? currentItem.title
              : `${currentItem.title.slice(0, maxTitleLength)}`
            }
            onBlur={renameTitle}
            onFocus={handleTitleFocus}
            autoFocus
          />
        ) : (
          <h1
            className="title"
            onClick={() => {
              if (isOwner || !clientReadOnly) {
                setTitleEditMode(true);
              }
            }}
          >
            {noteTitle ?? currentItem.title}
          </h1>
        )}
      </section>
      <section className="page-subtitle">
        <img
          src={noteEditorIcon}
          alt="note editor icon"
        />
        <span>Note</span>
      </section>
      <section className="page-body">
        {useFileUpload && currentItem.fileViewerUrl ? (
          <FileViewer
            url={currentItem.fileViewerUrl}
            fileExt={currentItem.file?.ext}
          />
        ) : useFileUpload && isEdit ? (
          <FileUploader
            handleChange={handleFileUpload}
            name="file"
            children={(
              <div className="page-content file-uploader">
                <div className="file-uploader-icon">
                  {loading ? <span className="file-uploader__spinner fa fa-spin fa-spinner" /> : (
                    <>
                      <img
                        src={UploaderIcon}
                        alt="uploader Icon"
                      />
                      <p className="file-uploader-description">
                        Drag your file here, or{' '}
                        <span onClick={handleFileUpload}>browse.</span>
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}
          />
        ) : (
          <Note
            isEdit={isEdit}
            isTemplate={isTemplate}
            jsonContent={content}
            html={html}
            showBrandingHeader={useDefaultHeader}
            branding={branding}
            brandingContent={brandingContent}
            updateNoteContent={updateNoteContent}
            toolbarProps={toolbarProps}
            metadata={metadata}
          />
        )}
        <div
          className={clsx('page-sidebar')}
        >
          <div className={clsx('page-sidebar__inner')}>
            <button
              className={clsx('button', 'button--sm', 'm-0', 'edit', editingLocked && 'inactive')}
              onClick={isEdit ? editDone : (editingLocked ? null : editModeChange)}
            >
              {isEdit ? 'Done' : (editingLocked ? 'Locked' : 'Edit')}
            </button>
            <div className="accordion-list">
              {!isEdit && !editingLocked ? (
                <>
                  <div className="view-only">
                    You're in view-only mode. Click above to enable editing capabilities.
                  </div>
                  {/* {!isTemplate && (
                    <Accordion title="Comments">
                      <Discussion
                        createComment={createComment}
                        updateComment={updateComment}
                        deleteComment={deleteComment}
                        comments={comments}
                        userId={userId}
                      />
                    </Accordion>
                  )} */}
                </>
            ) : isEdit && (
              <>
                <div className="note__view-options">
                  <div className="view_option view_option--enabled">
                    <Clickwrap
                      name="note-editor__file-upload"
                      checked={useFileUpload}
                      className="clickwrap--view-option small"
                      onChange={() => {
                        setUseFileUpload(!useFileUpload);
                        if (isTemplate) {
                          updateUseTemplateFileUpload({ id: itemId, useFile: !useFileUpload });
                        } else {
                          updateUseFileUpload({ id: itemId, useFile: !useFileUpload });
                        }
                      }}
                    />
                    <label htmlFor="note-editor__file-upload">
                      Use file upload
                    </label>
                  </div>
                  {useFileUpload && currentItem.fileViewerUrl && (
                    <div className="view_option view_option--enabled file-detail">
                      <div className="file-name">{filename}</div>
                      <DeleteButton
                        onClick={handleFileRemove}
                      />
                    </div>
                  )}
                  {!useFileUpload && (
                    <div className="view_option view_option--enabled">
                      <Clickwrap
                        name="note-editor__default-header"
                        checked={useDefaultHeader}
                        className="clickwrap--view-option small"
                        onChange={toggleDefaultHeader}
                      />
                      <label htmlFor="note-editor__default-header">
                        Use default header
                      </label>
                    </div>
                  )}
                </div>
                <Accordion title="Cover Image">
                  <ImageUploadField
                    label=''
                    imageUploadType='shortcut'
                    imageUrl={imageUrl}
                    originalImageKey={originalImageKey}
                    updateImageKey={updateImageKey}
                    updateImageUrl={setImageUrl}
                    updateOriginalImageKey={setOriginalImageKey}
                    imageInfoPath='wedding/note/images'
                    imageProcessPath='wedding/note/image'
                    setImageUpdating={setImageUpdating}
                    openCropper={showCropper}
                    cropperOnly={!!imageUrl}
                    setImageModalOpen={() => setShowCropper(!showCropper)}
                    imageCropperUploadText='Drag your image here'
                    cropperConfig={image?.cropPosition}
                    errorCallback={handleImageUploadError}
                  />
                  {isImageUpdating ? (
                    <div className="cover-image__upload">
                      <span className="cover-image__spinner fa fa-spin fa-spinner" />
                    </div>
                  ) : imageUrl ? (
                    <div className='cover-image__uploaded'>
                      <img
                        src={imageUrl}
                        alt="Cover Img"
                      />
                      <button
                        className="button--unstyled"
                        onClick={() => setShowCropper(true)}
                      >
                        Adjust Image
                      </button>
                    </div>
                  ) : <></>}
                  {(!isImageUpdating && imageError && imageError !== '') && (
                    <div className="file-upload-error">
                      {imageError}
                    </div>
                  )}
                </Accordion>
                {!isTemplate && (
                  <Accordion
                    title="Category"
                    className="note-editor__restriction"
                  >
                    <div className={clsx('ap-dropdown__menu-section')}>
                      <FormSelect
                        onChange={updateCategoryOption}
                        value={currentItem.categoryId}
                        name="Category"
                        className="note-restriction-dropdown"
                        scrollLockOnOpen={false}
                        wrapperProps={{ name: '' }}
                        labelHidden
                      >
                        {categoryOptions.map(item => (
                          <option
                            key={`move-to-${item.id}`}
                            value={item.id}
                          >
                            {item.category}
                          </option>
                        ))}
                      </FormSelect>
                    </div>
                  </Accordion>
                )}
                {!isClient && (
                  <Accordion
                    title="Restrictions"
                    className="note-editor__restriction"
                  >
                    <div className={clsx('ap-dropdown__menu-section')}>
                      <FormSelect
                        onChange={restrictionOnChange}
                        emptyValue="None"
                        value={restriction}
                        name="Restrictions"
                        className="note-restriction-dropdown"
                        scrollLockOnOpen={false}
                        wrapperProps={{ name: '' }}
                        labelHidden
                      >
                        {NOTE_RESTRICTIONS.map(item => (
                          <option
                            key={`move-to-${item.value}`}
                            value={item.value}
                          >
                            {item.label}
                          </option>
                        ))}
                      </FormSelect>
                    </div>
                  </Accordion>
                )}
                <Accordion
                  title="Activity"
                  className="note-editor__activity"
                >
                  {!isTemplate && (
                    <UserActivity
                      user={updatedBy}
                      date={updatedAt}
                      message="updated this document."
                    />
                  )}
                  <UserActivity
                    user={createdBy}
                    date={createdAt}
                    message="created this document."
                  />
                </Accordion>
                {/* {!isTemplate && (
                  <Accordion title="Comments">
                    <Discussion
                      createComment={createComment}
                      updateComment={updateComment}
                      deleteComment={deleteComment}
                      comments={comments}
                      userId={userId}
                    />
                  </Accordion>
                )} */}
                {canResetNote && (
                  <Accordion title="Admin">
                    <button
                      onClick={handleRevertNote}
                      className="button button--sm button--outline button--delete"
                    >
                      Reset note
                    </button>
                    <button
                      onClick={() => {
                        if (isTemplate)
                          window.location = `/app/project/${orgId}/settings/templates/note-templates/${itemId}`;
                        else
                          window.location = `/app/project/${projectId}/tools/notes/${itemId}/legacy`;
                      }}
                      className="button button--sm button--outline button--delete"
                    >
                      Go to V1 notes
                    </button>
                  </Accordion>
                )}
              </>
            )}
            </div>
          </div>
        </div>
      </section>
      <APModal
        open={openMultiUserModal}
        onClose={() => setMultiUserModalOpen(false)}
        hasHeader
        title="Editing Locked"
        className="ap-modal--edit"
        actions={(
          <button
            type="button"
            className="button"
            onClick={() => setMultiUserModalOpen(false)}
          >
            Got It
          </button>
        )}
      >
        {`Looks like ${currentEditorName} is editing this note right now.
        You’ll be able to edit once their changes have been saved and their edit window has been closed.`}
      </APModal>
      {noteError && (
        <APModal
          open={!!noteError}
          onClose={closeError}
          hasHeader
          title="Something went wrong."
          name="note-error"
          className="ap-modal--edit"
          actions={(
            <button
              type="button"
              className="button"
              onClick={closeError}
            >
              Got It
            </button>
        )}
        >
          {noteError?.message}
        </APModal>
      )}
      {showRevertWarning && (
        <APModal
          open
          width={350}
          height={175}
          onClose={() => setShowRevertWarning(false)}
          hasHeader={false}
          title="Are you sure?"
          name="revert-warning"
          className="ap-modal--delete-confirmation ap-modal--revert-warning"
          size="xs"
          actions={(
            <>
              <button
                onClick={() => setShowRevertWarning(false)}
                className="button button-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  revertNote();
                  setShowRevertWarning(false);
                }}
                className="button button-delete"
              >
                Ok
              </button>
            </>
          )}
        >
          Reverting this note will discard any changes that occurred after this note was migrated. Are you sure?
        </APModal>
      )}
      {showInactivityModal && (
        <InactivityModal
          dismissInactivityModal={dismissInactivityModal}
          resetTimer={startTicker}
          time={inactivityTime}
        />
      )}
    </div>
  );
};

const didNoteCrash = (noteIdParam, templateIdParam, lastUpdated) => {
  const recoveryId =
      templateIdParam ? `note-template-${templateIdParam}` : noteIdParam ? `note-${noteIdParam}` : null;
  if (recoveryId) {
    const { timestamp, content: recoveredNote } = getStorage(recoveryId) || {};

    if (!recoveredNote) return;

    const backupTime = dayjs(timestamp);
    // Time stored in the db is in UTC and isnt set from the front end.
    const updatedTime = dayjs(lastUpdated);

    // If local storage version is newer than the last save to db, likely there was a crash.
    if (updatedTime.isBefore(backupTime)) {
      return recoveredNote;
    }
  }
};

export default NoteEditorComponent;
